<template>
  <article
    class="tel-tile"
    :class="{ 'tel-tile--active': active }"
  >
     <div
        v-if="active"
        class="tel-tile__meta">
        <ag-button
          type="slim"
          class="tel-tile__link tel-tile__link--back"
          @click="$router.go(-1)"
        >
          <ag-icon
            class="tel-tile__link-icon"
            name="arrow-right"
          />
        </ag-button>
        <span class="tel-tile__meta-date">
          {{ cleanDate | formatDateLong }}
        </span>
        <span class="tel-tile__meta-time">
          {{ cleanDate | formatTime }}
        </span>
    </div>
    <header class="tel-tile__head">
      <h3
        class="tel-tile__headline"
        :class="{ 'tel-tile__headline--unread': unread, 'tel-tile__headline--active': active }"
      >
        {{ headline }}
      </h3>
      <time
        v-if="!active"
        class="tel-tile__date"
        :datetime="date">
        <span v-if="isToday">{{ cleanDate | formatTime(this.locale) }}</span>
        <span v-else>{{ cleanDate | formatDate(this.locale) }}</span>
      </time>
    </header>
    <div class="tel-tile__body">
      <p v-html="message"></p>
      <ul
        v-if="active && document.name && document.path"
        class="tel-tile__document-list">
        <li class="tel-tile__document-title">Dokument:</li>
        <li>
          <a
            download
            :href="`${document.path}${document.name}`"
            class="tel-tile__link tel-tile__link--download"
          >
            <ag-icon
              class="tel-tile__download-icon"
              name="paper-clip" />
            {{ document.name | trimcate }}
          </a>
        </li>
      </ul>
      <ul
        v-if="active"
        class="tel-tile__contact-list">
        <li class="tel-tile__contact-item">
          <span>Telefon: </span>
            <a
              v-if="callerPhone"
              class="tel-tile__contact-link"
              :href="`tel:${callerPhone}`"
            >
              {{ callerPhone }}
            </a>
            <ag-clipboard
              v-if="callerPhone"
              :value="callerPhone"
              class="tel-tile__copy"
            />
          <span
            v-else
            class="tel-tile__contact-link tel-tile__contact-link--inactive"
          >
            nicht angegeben
          </span>
        </li>
        <li class="tel-tile__contact-item">
          <span>Fax: </span>
          <span
            v-if="callerFax"
            class="tel-tile__contact-link"
          >
          {{ callerFax }}
          <ag-clipboard :value="callerFax" class="tel-tile__copy"/>
          </span>
          <span
            v-else
            class="tel-tile__contact-link tel-tile__contact-link--inactive"
          >
            nicht angegeben
          </span>
        </li>
        <li class="tel-tile__contact-item">
          <span>E-Mail: </span>
          <span
            v-if="callerMail"
          >
            <a
              class="tel-tile__contact-link"
              :href="`mailto:${callerMail}`"
            >
              {{ callerMail }}
            </a>
            <ag-clipboard :value="callerMail" class="tel-tile__copy"/>
          </span>
          <span
            v-else
            class="tel-tile__contact-link tel-tile__contact-link--inactive"
          >
            nicht angegeben
          </span>
        </li>
      </ul>
    </div>
    <footer
      v-if="!active"
      class="tel-tile__footer"
    >
      <a
        v-if="document.name && document.path"
        download
        :href="`${document.path}${document.name}`"
        class="tel-tile__link tel-tile__link--download"
      >
        <ag-icon
          class="tel-tile__download-icon"
          name="paper-clip" />
        {{ document.name | trimcate }}
      </a>
      <router-link
        class="tel-tile__link tel-tile__link--detail"
        :to="{ name: 'telephone-service.entry', params: { id: id }}">
        <ag-icon name="arrow-right" />
      </router-link>
    </footer>
    <footer
      v-if="active"
      class="tel-tile__footer tel-tile__footer--active"
    >
      <a
        class="tel-tile__cta"
        :class="{ 'tel-tile__cta--inactive' : !callerMail }"
        :href="`mailto:${callerMail}`"
      >
        E-Mail schreiben</a>
      <a
        class="tel-tile__cta"
        :class="{ 'tel-tile__cta--inactive' : !callerPhone }"
        :href="`tel:${callerPhone}`"
      >
        Rückruf
      </a>
    </footer>
  </article>
</template>

<script>
import trimcate from 'trimcate';

import AgIcon from '@/components/shared/AgIcon.vue';
import AgButton from '@/components/shared/AgButton.vue';
import AgClipboard from '@/components/shared/AgClipboard.vue';

/**
 * @vuese
 * a telephone list entry, can be switched via `active` flag into
 * telephone detail view.
 */
export default {
  name: 'telephone-tile',
  components: {
    AgIcon,
    AgButton,
    AgClipboard,
  },
  props: {
    /**
     * tiles headline
     */
    headline: {
      type: String,
      required: true,
    },
    /**
     * a string with a datetime
     */
    date: {
      type: String,
      required: true,
    },
    /**
     * flag if message was read, unused
     */
    unread: {
      type: Boolean,
      default: false,
    },
    /**
     * message from caller
     */
    message: {
      type: String,
      default: 'Dies ist ein Beispieltext, damit man sehen kann wie diese Kachel dargestellt wird.',
    },
    /**
     * phone number of caller, but as String, you never know
     */
    callerPhone: {
      type: String,
      default: null,
    },
    /**
     * fax number of caller, but as String, you never know
     */
    callerFax: {
      type: String,
      default: null,
    },
    /**
     * e-mail address of caller
     */
    callerMail: {
      type: String,
      default: null,
    },
    /**
     * call id
     */
    id: {
      type: Number,
      required: true,
    },
    /**
     * toggle between list mode and detail mode
     */
    active: {
      type: Boolean,
      default: false,
    },
    /**
     * users locale for date formatting
     */
    locale: {
      type: String,
      default: 'de-DE',
    },
    /**
     * an object of a downloadable document
     */
    document: {
      type: Object,
      default: () => {},
    },
    /**
     * if `message` is long you can provide here after how many
     * characters it should be trimcate'd
     * 0 means no trimcation
     */
    truncateLongText: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    /**
     * check if given date is today
     * @return {Boolean} true or false
     */
    isToday() {
      const today = new Date();
      const x = new Date(this.date);
      return (today.toDateString() === x.toDateString());
    },
    /**
     * iOs date string preparation
     * @return {String} iOs compatible date string
     */
    cleanDate() {
      return this.date.substr(0, this.date.indexOf('.'));
    },
  },
  filters: {
    /**
     * special date formatting. Todays date will return `Heute`
     * yesterdays date will return `Gestern` all other dates will return
     * dd.mm.yy
     * @arg {String} date - a date string
     * @arg {String} locale - users locale
     */
    formatDate(date, locale = 'de') {
      const today = new Date();
      const x = new Date(date);
      if (today.toLocaleDateString() === x.toLocaleDateString) {
        return 'Heute';
      }
      today.setDate(today.getDate() - 1);
      if (today.toLocaleDateString() === x.toLocaleDateString()) {
        return 'Gestern';
      }
      return x.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: '2-digit' });
    },
    /**
     * format date like dd.mm.yyyy
     * @arg {String} date - a date string
     * @arg {String} locale - users locale
     */
    formatDateLong(date, locale = 'de') {
      const x = new Date(date);
      return x.toLocaleDateString(locale);
    },
    /**
     * formats time we want it to
     * @arg {String} date - a date string
     * @arg {String} locale - users locale
     * @return {String} a time
     */
    formatTime(date, locale = 'de') {
      const x = new Date(date);
      return `${x.toLocaleTimeString(locale).substring(0, 5)} Uhr`;
    },
    /**
     * take a text and return this first prefixLength characters and the last
     * suffixLength characters. If prefixLength is 0 whole text is returned
     * @param {String} text - text you want to trimcate
     * @param {Number} prefixLength - character count you want to preserve from string start
     * @param {Number} suffixLength - character cound you want to preserve from string end
     */
    trimcate(text, prefixLength = 10, suffixLength = 6) {
      return trimcate(text, {
        prelude: prefixLength,
        postlude: suffixLength,
        wholeWhords: true,
      });
    },
  },
};
</script>

<style lang="scss">
.tel-tile {
  background-color: #fff;
  box-shadow: 0 0 10px rgba(17, 42, 122, .161);

  &--active {
    margin-bottom: em(20px);
  }

  &__contact-link {
    $font-size: 14px;
    font-size: rem($font-size);
    color: $text-color;
    font-weight: 600;

    &--inactive {
      color: #999;
    }
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: em(13px) em(12px) 0 em(15px);
  }

  &__meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: em(12px);
    padding: em(13px) em(12px) 0 em(15px);
  }

  &__meta-date {
    $font-size: 13px;
    font-size: rem($font-size);
    color: #666;
    margin-right: em(10px, $font-size);
  }
  &__meta-time {
    $font-size: 13px;
    font-size: rem($font-size);
    color: $text-color;
    font-weight: 600;
  }

  &__document-list {
    list-style: none;
    margin: 0;
    padding: em(14px) 0;
  }

  &__document-title {
    $font-size: 14px;
    font-size: rem($font-size);
    font-weight: 600;
    color: #666;
    line-height: 1.5;
    padding-bottom: em(7px, $font-size);
  }

  &__contact-list {
    $font-size: 14px;
    list-style: none;
    margin: em(4px, $font-size) 0 0 0;
    padding: 0;
  }

  &__contact-item {
    $font-size: 14px;
    padding: 0 0 em(4px, $font-size) 0;
    display: flex;
    align-items: center;
    > span {
      display: block;
      margin-right: .3em;
    }
  }

  &__headline {
    $font-size: 14px;
    font-size: rem($font-size);
    font-weight: 700;
    color: $primary-color;
    font-family: $copy-font;
    margin: 0;
    line-height: 1.35;
    position: relative;
    margin-left: em(18px, $font-size);

    &::before {
      content: "";
      display: block;
      position: absolute;
      top: em(5px, $font-size);
      left: em(-15px, $font-size);
      width: em(7px, $font-size);
      height: em(7px, $font-size);
      border-radius: 50%;
      background-color: #dadada;
    }

    &--unread {
      &::before {
        background-color: $tertiary-color;
      }
    }

    &--active {
      $font-size: 15px;
      margin-left: 0;
      font-weight: 600;
      font-size: rem($font-size);

      &::before {
        content: none;
      }
    }
  }

  &__date {
    $font-size: 13px;
    font-size: rem($font-size);
    text-align: right;
    line-height: 1.6;
  }

  &__body {
    $font-size: 14px;
    font-size: rem($font-size);
    line-height: 1.5;
    margin: em(15px, $font-size) 0 em(22px, $font-size) 0;
    padding: 0 em(12px) 0 em(15px);
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__link {
    &--detail {
      margin-left: auto;
      width: em(40px);
      height: em(40px);
      border-radius: 50%;
      background-color: #3169AA;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;

      &:active,
      &:visited {
        color: #fff;
      }
    }
    &--download {
      $font-size: 12px;
      background-color: #f1f1f1;
      border-radius: 10px;
      padding: em(6px, $font-size) em(10px, $font-size);
      font-weight: 600;
      color: #3169AA;
      display: inline-flex;
      align-items: center;
    }

    &--back {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f1f1f1;
      color: #939598;
      border-radius: 50%;
      padding: 0;
      width: em(40px);
      height: em(40px);
      margin-right: auto;

      &:visited {
        color: #939598;
      }
    }
  }

  &__link-icon {
    transform: rotate(180deg);
  }

  &__download-icon {
    $font-size: 18px;
    font-size: rem($font-size);
    margin-right: em(6px, $font-size);
    color: #666;
  }

  &__footer {
    padding: em(13px) em(12px) em(12px) em(15px);

    &--active {
      padding: em(18px) em(15px) em(17px) em(15px);
      background-color: #f8f8f8;
      display: flex;
    }
  }

  &__cta {
    $font-size: 14px;
    font-size: rem($font-size);
    width: 100%;
    font-weight: 700;
    text-align: center;
    padding: em(10px, $font-size) em(20px, $font-size);
    background-color: #fff;
    border: 2px solid $tertiary-color;
    border-radius: 5px;
    color: $primary-color;
    white-space: nowrap;

    &:first-child {
      margin-right: em(10px, $font-size);
    }

    &--inactive {
      pointer-events: none;
      color: #999;
      border-color: #ccc;
    }
  }
  &__copy {
    color: #444 !important;
    width: auto;
    margin-left: rem(5px);
  }
}
</style>
